/**
 * Projects are the unit of work the rest of the site hangs off: the hero rail
 * lists them, each Focus Area page highlights its own, and every story, article,
 * report and gallery item can be attributed to one.
 *
 * `program` matches a Focus Area slug, which is what the filter tabs switch on.
 */

import { type DocCollection } from './resources';
import type { FocusAttachments } from './focusContent';
import type { ArticleItem, DownloadItem, GalleryItem } from '@components/pages/ResourceSections';

export type ProjectStatus = 'Active' | 'Pilot' | 'Completed';

export interface ProjectStat {
    value: string;
    label: string;
}

export interface Project {
    slug: string;
    name: string;
    /** One line, used on the card and under the detail hero. */
    tagline: string;
    /**
     * Focus Area slugs this project sits under. A project can serve more than one
     * programme — a duty-solicitor scheme is both access to justice and judicial
     * accountability — so this is a list, and the first entry is the primary one
     * used for the breadcrumb.
     */
    programs: string[];
    status: ProjectStatus;
    /** Position in the homepage hero rail. 0 = not in it. */
    heroSlot: number;
    /**
     * Position in each programme page's highlighted rail, keyed by slug.
     * A missing or 0 entry means it is not on that page's rail.
     */
    programSlots: Record<string, number>;
    image: string;
    /** Product mark, from `@podus/ui/logo-icons`. Shown on the card and detail hero. */
    logo: string;
    /** Where the product itself lives, if it has a public home. */
    website?: string;
    started: string;
    /** Where it runs, shown in the at-a-glance strip. */
    coverage: string;
    /** Headline numbers on the detail page. */
    stats: ProjectStat[];
    /** "The Problem" — what was wrong before this existed. */
    problem: string[];
    /** "Our Solution" — what the project actually does about it. */
    solution: string[];
    /** The commitments the project is measured against. */
    objectives: string[];
    /** Set once the CMS has answered; from then on it alone decides what the page shows. */
    attached?: FocusAttachments;
}

export const PROGRAMS: { slug: string; label: string }[] = [
    { slug: 'tech-for-justice', label: 'Tech For Justice' },
    { slug: 'access-to-justice', label: 'Access To Justice' },
    { slug: 'judicial-accountability', label: 'Judicial Accountability' },
    { slug: 'democratic-governance', label: 'Democratic Governance' },
    { slug: 'sgbv', label: 'SGBV' },
];

export const programLabel = (slug: string): string => PROGRAMS.find((p) => p.slug === slug)?.label ?? slug;

/**
 * Every project the site knows. The CMS owns this list once it has answered — a
 * published project is added, a drafted one removed — so the bundled ten are
 * the offline fallback, not the ceiling.
 */
/**
 * Every project, as the CMS publishes it. The bundled set that used to live here
 * attached its articles, stories, gallery and documents *by index* into the
 * sample pools; the CMS attaches them by reference instead, resolved into
 * `attached` when the feed lands.
 */
export const PROJECTS: Project[] = [];

export const getProject = (slug?: string): Project | undefined => PROJECTS.find((p) => p.slug === slug);

export const projectStories = (p: Project): ArticleItem[] => p.attached?.stories ?? [];
export const projectArticles = (p: Project): ArticleItem[] => p.attached?.articles ?? [];
export const projectGallery = (p: Project): GalleryItem[] => p.attached?.gallery ?? [];

/** Documents per collection, as the CMS attached them. */
export const projectDocuments = (p: Project): Record<DocCollection, DownloadItem[]> =>
    p.attached?.documents ?? { publications: [], reports: [], infographics: [] };

/* ── Featured rails ───────────────────────────────────────────────────── */

/** The homepage hero rail, in slot order. The CMS owns this ordering. */
export const HERO_PROJECTS: Project[] = PROJECTS.filter((p) => p.heroSlot > 0).sort((a, b) => a.heroSlot - b.heroSlot);

/** The highlighted rail on one Focus Area page, in that page's own slot order. */
export const featuredInProgram = (program: string): Project[] =>
    PROJECTS.filter((p) => (p.programSlots[program] ?? 0) > 0).sort(
        (a, b) => a.programSlots[program] - b.programSlots[program],
    );

/** The primary programme, used where only one can be shown. */
export const primaryProgram = (p: Project): string => p.programs[0] ?? '';
